import { cn } from '@/lib/utils';
import { ChevronRight, ExternalLink } from 'lucide-react';

import SourceTypeIcon from '@/components/icons/SourceTypeIcon';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';

import { useIsMobile } from '@/hooks/use-mobile';

export interface SourceInfo {
    name: string;
    link?: string | null;
    configType?: string | null;
}

interface DMSourceLinkProps {
    sources: SourceInfo[];
}

const SourceAvatar = ({ configType }: { configType?: string | null }) => (
    <div className="flex justify-center items-center bg-background -mr-3 last:mr-0 p-[5px] border border-border rounded-full [&_svg]:size-3">
        <SourceTypeIcon configType={configType} className="size-3.5" />
    </div>
);

const DMSourceLink = ({ sources }: DMSourceLinkProps) => {
    const isMobile = useIsMobile();

    if (!sources.length) return null;

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="default"
                    className={cn(
                        'group flex items-center gap-2.5',
                        'pl-1 pr-3 py-2'
                    )}
                >
                    <div className="flex items-center">
                        {sources.map((source) => (
                            <SourceAvatar
                                key={source.name}
                                configType={source.configType}
                            />
                        ))}
                    </div>
                    <span className="font-medium text-muted-foreground group-data-[state=open]:text-foreground group-hover:text-foreground text-xs leading-4 transition-colors">
                        Sources
                    </span>

                    <ChevronRight className="opacity-0 group-data-[state=open]:opacity-100 group-hover:opacity-100 text-muted-foreground transition-all -translate-x-2 group-data-[state=open]:translate-x-0 group-hover:translate-x-0 duration-150" />
                </Button>
            </PopoverTrigger>

            <PopoverContent
                align="end"
                side={isMobile ? 'top' : 'right'}
                sideOffset={8}
                className="bg-popover slide-in-from-left-2 p-3 px-4 rounded-lg w-auto min-w-[180px] max-w-[320px] animate-in duration-200 fade-in-0 zoom-in-95"
            >
                <div className="flex flex-col gap-3">
                    {sources.map((source) => (
                        <a
                            key={source.name}
                            className="flex justify-between items-center gap-2 w-full"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={source.link ?? undefined}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="flex justify-center items-center bg-background p-1.5 border border-border rounded-full shrink-0">
                                    <SourceTypeIcon
                                        configType={source.configType}
                                        className="size-3.5"
                                    />
                                </div>
                                <span className="font-medium text-foreground text-sm truncate">
                                    {source.name}
                                </span>
                            </div>
                            {source.link ? (
                                <a
                                    href={source.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:bg-popover text-secondary-foreground hover:text-foreground transition-colors"
                                >
                                    <ExternalLink className="size-4" />
                                </a>
                            ) : (
                                <span className="text-secondary-foreground text-sm whitespace-nowrap shrink-0">
                                    No link available
                                </span>
                            )}
                        </a>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default DMSourceLink;
